import React, { type PropsWithChildren } from 'react';
import {
  type ComponentProps,
  type EmbeddedReportElement,
  SelectionRectangle,
  Resizer,
} from '@aha-app/aha-editor-v2';
import EmbeddedReportPanel from './EmbeddedReportPanel';
import styles from './EmbeddedReport.module.css';
import classnames from 'classnames';
import { PublishingContext } from 'components/Dashboards/contexts';
import ReportProvider from './ReportProvider';
import dashboardStyles from 'components/Dashboards/Dashboards.module.css';

interface EmbeddedReportComponentProps
  extends ComponentProps<EmbeddedReportElement> {}

const EmbeddedReportComponent: React.FC<
  PropsWithChildren<EmbeddedReportComponentProps>
> = ({ attributes, element, children }) => {
  const width = element.resizer_width || 900;
  const height = element.resizer_height || 500;

  const fullWidth = element.payload.fullWidth;
  const content = (
    <div
      {...attributes}
      className={classnames('embedded-report-reset', styles.Panel, {
        [dashboardStyles.PanelFullWidth]: fullWidth,
        [styles.PanelFullWidth]: fullWidth,
        PanelFullWidth: fullWidth, // Needed a hook that is not a .module.css scoped style
      })}
      id={`Dashboard-Panel-${element.bookmark_id}`}
      contentEditable={false}
      style={{ height }}
    >
      <div className={styles.PanelTitle}>
        <span className='text-050'>{element.payload.name || '\u00a0'}</span>
      </div>
      <PublishingContext.Provider
        value={{
          // disabling this for now as the editor forces a refresh which
          // causes the panels to be blank when shared.
          isSharedWebpage: false, // window.parent.isSharedWebpage,
          publishing: window.parent.isPublishing,
          liveView: false,
        }}
      >
        <ReportProvider>
          <EmbeddedReportPanel
            payload={element.payload}
            width={width}
            height={height}
          />
        </ReportProvider>
      </PublishingContext.Provider>
      {children}
    </div>
  );
  if (window.isPublishing && fullWidth) {
    return content;
  }

  return (
    <SelectionRectangle inline>
      <Resizer
        width={width}
        height={height}
        originalWidth={element.resizer_originalWidth}
        originalHeight={element.resizer_originalHeight}
        element={element}
        handles={['se']}
        keepRatio={false}
        showPixelSize
        liveResize
        fullWidth={fullWidth}
      >
        {content}
      </Resizer>
    </SelectionRectangle>
  );
};

export default EmbeddedReportComponent;
